import Head from 'next/head';
import { GetServerSideProps } from 'next';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  NormalizedCacheObject,
  ApolloError,
} from '@apollo/client';
import jwt from 'jsonwebtoken';
import Widget from '../../components/review-widget/Widget';
import { ReviewWidgetQuery } from '../../queries';
import { ReviewWidget } from '../../types';
import ErrorSection from '../../components/ErrorSection';
import { errorMessages } from '../../utils/errorMessages';

type Decoded = {
  tenant: string;
  host: string;
  store: number;
  widget: number;
  iat: number;
};

type Props = {
  data: {
    reviewWidget: ReviewWidget;
  };
  token: string;
  decoded: Decoded;
  apolloState: NormalizedCacheObject;
};

type ErrorProps = { error: { graphQL?: ApolloError[]; other?: string } };

const ReviewWidgetContainer = (props: Props | ErrorProps): JSX.Element => {
  if ('error' in props) {
    if (
      props.error.graphQL &&
      props.error.graphQL.length > 0 &&
      errorMessages.has(props.error.graphQL[0].message)
    ) {
      const defaultErrorMessage = 'There was an error loading the review widget.';
      const errorMessage = errorMessages.get(props.error.graphQL[0].message) ?? defaultErrorMessage;

      return <ErrorSection errorMessage={errorMessage} />;
    }

    return null;
  }

  const { data, token, decoded, apolloState } = props;

  const client = new ApolloClient({
    ssrMode: typeof window === 'undefined',
    uri: `https://${decoded.host}/public_graphql`,
    cache: new InMemoryCache().restore(apolloState),
    headers: {
      ['X-RS-TOKEN']: token as string,
      ['Access-Control-Allow-Credentials']: 'true',
    },
  });

  if (data.reviewWidget.richSnippet && typeof window !== 'undefined') {
    window['iFrameResizer'] = {
      onReady: () => {
        window['parentIFrame'].sendMessage({
          businessName: data.reviewWidget.businessName,
          averageRating: data.reviewWidget.reviewSummary.averageRating,
          count: data.reviewWidget.reviewSummary.count,
        });
      },
    };
  }

  return (
    <ApolloProvider client={client}>
      <Head>
        <title>Review Widget</title>
      </Head>

      <Widget data={data.reviewWidget} token={token} />
      <style>{data.reviewWidget.customCss}</style>
    </ApolloProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { token } = context.params;
  const decoded = jwt.verify(token as string, process.env.PUBLIC_GQL_SECRET, {
    algorithms: ['HS256'],
  }) as Decoded;

  const client = new ApolloClient({
    ssrMode: typeof window === 'undefined',
    uri: `https://${decoded.host}/public_graphql`,
    cache: new InMemoryCache(),
    headers: {
      ['X-RS-TOKEN']: token as string,
      ['Access-Control-Allow-Credentials']: 'true',
    },
  });

  try {
    const { data } = await client.query({
      query: ReviewWidgetQuery,
      variables: {
        token: token,
      },
    });

    const apolloState = client.extract();

    return { props: { data, token, decoded, apolloState } };
  } catch (error) {
    if (error instanceof ApolloError) {
      return {
        props: {
          error: { graphQL: error.graphQLErrors },
        },
      };
    }

    return {
      props: { error: { other: error } },
    };
  }
};

export default ReviewWidgetContainer;
